
import React from 'react';
import { FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Receipt } from "lucide-react";
import { Control } from 'react-hook-form';
import { z } from 'zod';
import { Checkbox } from "@/components/ui/checkbox";

// Form schema for invoice data with conditional properties
export const invoiceDataSchema = z.object({
  companyName: z.string().min(2, "Nazwa firmy musi mieć co najmniej 2 znaki").optional(),
  nip: z.string().regex(/^\d{10}$/, "NIP musi składać się z 10 cyfr").optional(),
  invoiceStreet: z.string().min(3, "Podaj prawidłową nazwę ulicy"),
  invoiceBuildingNumber: z.string().min(1, "Podaj numer budynku"),
  invoiceApartmentNumber: z.string().optional(),
  invoiceCity: z.string().min(2, "Podaj prawidłową nazwę miasta"),
  invoicePostalCode: z.string().regex(/^\d{2}-\d{3}$/, "Kod pocztowy musi być w formacie XX-XXX"),
  usePersonalDataForInvoice: z.boolean().default(false),
});

export type InvoiceDataValues = z.infer<typeof invoiceDataSchema>;

interface InvoiceDataSectionProps {
  control: Control<any>;
  onUsePersonalData: () => void;
  isUsePersonalDataChecked: boolean;
  installationType: 'home' | 'company';
}

const InvoiceDataSection: React.FC<InvoiceDataSectionProps> = ({ 
  control,
  onUsePersonalData,
  isUsePersonalDataChecked,
  installationType
}) => {
  return (
    <div>
      <div className="flex items-center space-x-2 mb-4">
        <Receipt className="h-5 w-5 text-gray-500" />
        <h3 className="text-lg font-medium">Dane do faktury</h3>
      </div>

      <div className="mb-4 flex items-start space-x-2">
        <FormField
          control={control}
          name="usePersonalDataForInvoice"
          render={({ field }) => (
            <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
              <FormControl>
                <Checkbox
                  checked={field.value}
                  onCheckedChange={(checked) => {
                    field.onChange(checked);
                    if (checked) {
                      onUsePersonalData();
                    }
                  }}
                />
              </FormControl>
              <div className="space-y-1 leading-none">
                <FormLabel>
                  Użyj danych osobowych i adresu z formularza
                </FormLabel>
              </div>
            </FormItem>
          )}
        />
      </div>
      
      {/* Company-specific fields */}
      {installationType === 'company' && (
        <div className="space-y-4">
          <FormField
            control={control}
            name="companyName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Nazwa firmy</FormLabel>
                <FormControl>
                  <Input placeholder="Nazwa firmy" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={control}
            name="nip"
            render={({ field }) => (
              <FormItem>
                <FormLabel>NIP</FormLabel>
                <FormControl>
                  <Input placeholder="0000000000" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      )}
      
      {!isUsePersonalDataChecked && (
        <>
          <div className="mt-4">
            <FormField
              control={control}
              name="invoiceStreet"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Ulica</FormLabel>
                  <FormControl>
                    <Input placeholder="Marszałkowska" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4 mt-4">
            <FormField
              control={control}
              name="invoiceBuildingNumber"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Numer budynku</FormLabel>
                  <FormControl>
                    <Input placeholder="42" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={control}
              name="invoiceApartmentNumber"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Numer lokalu (opcjonalnie)</FormLabel>
                  <FormControl>
                    <Input placeholder="15" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <FormField
              control={control}
              name="invoiceCity"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Miasto</FormLabel>
                  <FormControl>
                    <Input placeholder="Warszawa" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={control}
              name="invoicePostalCode"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Kod pocztowy</FormLabel>
                  <FormControl>
                    <Input placeholder="00-123" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default InvoiceDataSection;
